import { projectForReaders } from './connectedBible'
import { slugify, wordCount } from './model'
import { downloadBlob } from './download'
import type { StoryProject } from './types'
import type { Playthrough } from './playthrough'
import { describeCondition, describeEffect, displayValue } from './storyLogic'

export function downloadTextFile(
  text: string,
  name: string,
  type = 'text/plain;charset=utf-8',
) {
  downloadBlob(new Blob([text], { type }), name)
}

// Quote every CSV field and neutralize spreadsheet formulas in user-authored text.
export function csvCell(value: string | number) {
  let text = String(value)
  if (/^[\s]*[=+@-]/u.test(text) || /^[\t\r\n]/u.test(text)) text = "'" + text
  return '"' + text.replaceAll('"', '""') + '"'
}

export type ScriptExportOptions = {
  dialogueOnly?: boolean
  speaker?: string
  nodeIds?: string[]
}

export function scriptCsv(
  project: StoryProject,
  options: ScriptExportOptions = {},
) {
  project = projectForReaders(project)
  const rows: Array<Array<string | number>> = [
    [
      'line_id',
      'localization_key',
      'scene_id',
      'scene_title',
      'chapter',
      'order',
      'kind',
      'speaker',
      'direction',
      'text',
      'words',
      'destination_id',
      'condition_match',
      'conditions',
      'changes',
      'translation',
    ],
  ]
  for (const node of project.nodes) {
    if (options.nodeIds && !options.nodeIds.includes(node.id)) continue
    const prefix = (id: string) => [
      id,
      'line.' + id,
      node.id,
      node.title,
      node.chapter ?? '',
    ]
    node.blocks.forEach((block, index) => {
      if (
        (options.dialogueOnly || options.speaker) &&
        block.kind !== 'dialogue'
      )
        return
      if (
        options.speaker &&
        block.speaker?.trim().toLocaleLowerCase() !==
          options.speaker.trim().toLocaleLowerCase()
      )
        return
      rows.push([
        ...prefix(block.id),
        index + 1,
        block.kind,
        block.speaker ?? '',
        block.direction ?? '',
        block.text,
        wordCount(block.text),
        '',
        '',
        '',
        '',
        '',
      ])
    })
    if (!options.dialogueOnly && !options.speaker && node.kind !== 'ending')
      node.choices.forEach((choice, index) => {
        rows.push([
          ...prefix(choice.id),
          node.blocks.length + index + 1,
          'choice',
          '',
          '',
          choice.label,
          wordCount(choice.label),
          choice.targetId ?? '',
          choice.conditions?.length ? (choice.conditionMode ?? 'all') : '',
          (choice.conditions ?? [])
            .map((condition) =>
              describeCondition(condition, project.variables ?? []),
            )
            .join('; '),
          (choice.effects ?? [])
            .map((effect) => describeEffect(effect, project.variables ?? []))
            .join('; '),
          '',
        ])
      })
  }
  return (
    '\uFEFF' +
    rows.map((row) => row.map(csvCell).join(',')).join('\r\n') +
    '\r\n'
  )
}

export function runtimeStory(project: StoryProject) {
  project = projectForReaders(project)
  return {
    format: 'bramble-runtime',
    formatVersion: 1,
    id: project.id,
    title: project.title,
    startNodeId: project.startNodeId,
    variables: (project.variables ?? []).map(
      ({ id, name, type, initialValue }) => ({
        id,
        name,
        type,
        initialValue,
      }),
    ),
    nodes: project.nodes.map((node) => ({
      id: node.id,
      title: node.title,
      kind: node.kind,
      blocks: node.blocks,
      effects: node.effects ?? [],
      nextId:
        node.kind === 'ending' || node.choices.length ? null : node.nextId,
      choices:
        node.kind === 'ending'
          ? []
          : node.choices.map((choice) => ({
              ...choice,
              conditions: choice.conditions ?? [],
              conditionMode: choice.conditionMode ?? 'all',
              unavailableMode: choice.unavailableMode ?? 'disable',
              effects: choice.effects ?? [],
            })),
    })),
  }
}

export function playtestReport(
  project: StoryProject,
  play: Playthrough,
  coverage: { scenes: Set<string>; choices: Set<string> },
) {
  const lines = [
    '# Playtest · ' + project.title,
    '',
    'Started from: ' +
      (project.nodes.find((node) => node.id === play.originId)?.title ??
        play.originId),
    '',
    '## Current route',
    '',
  ]
  ;[...play.history, play].forEach((frame, index) => {
    const node = project.nodes.find((item) => item.id === frame.nodeId)
    const choice =
      index < play.history.length
        ? node?.choices.find((item) => item.id === frame.choiceId)
        : undefined
    lines.push(
      `${index + 1}. ${node?.title ?? frame.nodeId}${choice ? ' → ' + choice.label : ''}`,
    )
  })
  lines.push('', '## Current state', '')
  for (const variable of project.variables ?? [])
    lines.push(`- ${variable.name}: ${displayValue(play.state[variable.id])}`)
  if (!project.variables?.length) lines.push('No story variables.')
  lines.push(
    '',
    '## Coverage this session',
    '',
    `${coverage.scenes.size} / ${project.nodes.length} scenes visited.`,
    '',
    'Untested choices:',
  )
  let untested = 0
  for (const node of project.nodes.filter((item) => item.kind !== 'ending'))
    for (const choice of node.choices) {
      if (!coverage.choices.has(node.id + ':' + choice.id)) {
        untested++
        lines.push(`- ${node.title}: ${choice.label || '[Untitled choice]'}`)
      }
    }
  if (!untested) lines.push('None.')
  lines.push(
    '',
    'Coverage records manual playtesting in this session, including earlier attempts. It is not a proof that every possible state is reachable.',
    '',
  )
  if (play.errors.length)
    lines.push(
      '## Problems encountered',
      '',
      ...play.errors.map((error) => '- ' + error),
      '',
    )
  return lines.join('\n')
}

export function downloadPlaytestReport(
  project: StoryProject,
  play: Playthrough,
  coverage: { scenes: Set<string>; choices: Set<string> },
) {
  downloadTextFile(
    playtestReport(project, play, coverage),
    slugify(project.title, 'story') + '-playtest.md',
    'text/markdown;charset=utf-8',
  )
}
